import React, { useState, useEffect, useContext } from "react";
import { navigate } from "@reach/router";
import axios from "axios";
import { AuthContext } from "../helpers/AuthContext";
import Sidebar from "../components/Sidebar";
import "../styles/responsive.css";

// Admin screen to keep lab names clean for the lab competition. Users pick
// their lab from a suggestion list at signup/profile, but near-duplicates can
// still appear ("Gramann Lab" vs "Gramann Group"). Here an admin selects the
// duplicates, types the name to keep, and merges them — every matching user's
// `lab` is rewritten, so the leaderboard groups them as one lab.
const AdminLabs = () => {
  const context = useContext(AuthContext);
  const [labs, setLabs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState({}); // exact-name -> bool
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const headers = () => (context.authHeader ? context.authHeader() : {});

  const load = () => {
    setLoading(true);
    setError("");
    axios
      .get("/api/admin/labs", { headers: headers() })
      .then((res) => setLabs((res.data && res.data.labs) || []))
      .catch((e) =>
        setError(e?.response?.data?.message || e.message || "Failed to load labs")
      )
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (context.Auth !== "Admin") return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.Auth]);

  if (context.Auth !== "Admin") {
    return (
      <div className="results-page">
        <Sidebar />
        <div className="content-with-sidebar">
          <div style={{ padding: 24 }}>
            <h2>Admins only</h2>
            <button
              className="sci-btn sci-btn--primary"
              onClick={() => navigate("/dashboard")}
              style={{ marginTop: 12 }}
            >
              Back to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const toggle = (name) =>
    setSelected((s) => ({ ...s, [name]: !s[name] }));
  const selectedNames = Object.keys(selected).filter((n) => selected[n]);

  const doMerge = () => {
    const to = target.trim();
    if (!to) {
      setMsg("Type the lab name to keep (the target).");
      return;
    }
    if (selectedNames.length === 0) {
      setMsg("Tick at least one lab to merge/rename.");
      return;
    }
    setBusy(true);
    setMsg("");
    axios
      .post(
        "/api/admin/merge-labs",
        { from: selectedNames, to },
        { headers: headers() }
      )
      .then((res) => {
        const n = res.data && res.data.modified;
        setMsg(`✓ Updated ${n} user${n === 1 ? "" : "s"} → “${res.data.to}”.`);
        setSelected({});
        setTarget("");
        load();
      })
      .catch((e) =>
        setMsg(e?.response?.data?.message || e.message || "Merge failed")
      )
      .finally(() => setBusy(false));
  };

  const cell = { padding: "8px 10px", borderBottom: "1px solid #e5e7eb", textAlign: "left" };

  return (
    <div className="results-page">
      <Sidebar />
      <div className="content-with-sidebar">
        <div style={{ padding: 24, maxWidth: 820 }}>
          <h1 style={{ marginBottom: 4 }}>Manage Labs</h1>
          <p style={{ color: "#64748b", marginBottom: 20 }}>
            Keep each lab's competition score together. Tick the lab names that
            are really the same lab, type the name to keep, and merge them —
            every matching user is moved to that name.
          </p>

          {error && (
            <div style={{ color: "crimson", marginBottom: 12 }}>{error}</div>
          )}

          {loading ? (
            <div style={{ color: "#64748b" }}>Loading…</div>
          ) : !labs || labs.length === 0 ? (
            <div style={{ color: "#64748b" }}>
              No labs entered yet. Labs appear here once users set one at signup
              or under “Edit profile”.
            </div>
          ) : (
            <>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 14,
                }}
              >
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    <th style={{ ...cell, width: 40 }}></th>
                    <th style={cell}>Lab</th>
                    <th style={{ ...cell, width: 90, textAlign: "right" }}>
                      Members
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {labs.map((l) => (
                    <tr key={l.name}>
                      <td style={cell}>
                        <input
                          type="checkbox"
                          checked={!!selected[l.name]}
                          onChange={() => toggle(l.name)}
                        />
                      </td>
                      <td style={cell}>
                        {l.name}
                        {l.variants && l.variants.length > 1 && (
                          <span
                            style={{
                              fontSize: 12,
                              color: "#b45309",
                              marginLeft: 8,
                            }}
                            title={l.variants.join(" · ")}
                          >
                            ({l.variants.length} spellings)
                          </span>
                        )}
                      </td>
                      <td style={{ ...cell, textAlign: "right" }}>{l.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div
                style={{
                  marginTop: 20,
                  padding: 16,
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  background: "#f8fafc",
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 8 }}>
                  Merge / rename selected ({selectedNames.length})
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <input
                    list="lab-names"
                    className="sci-input"
                    style={{ flex: "1 1 260px" }}
                    placeholder="Name to keep (e.g. Gramann Lab)"
                    value={target}
                    onChange={(e) => setTarget(e.target.value)}
                  />
                  <datalist id="lab-names">
                    {labs.map((l) => (
                      <option key={l.name} value={l.name} />
                    ))}
                  </datalist>
                  <button
                    className="sci-btn sci-btn--primary"
                    onClick={doMerge}
                    disabled={busy}
                  >
                    {busy ? "Applying…" : "Merge / Rename"}
                  </button>
                </div>
                {msg && (
                  <div style={{ marginTop: 10, color: "#0f766e" }}>{msg}</div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminLabs;
